import { trigger, state, style, animate, transition } from '@angular/animations';
import { saveAs } from 'file-saver';
import { ColumnDefinition } from 'src/app/shared/interfaces/prod-plan-model';

export class CommonHandler {
  constructor() { }

  public static fadeAnimation: any = trigger('fadeAnimation', [
    transition(':enter', [style({ opacity: 0 }), animate('0.5s', style({ opacity: 1 }))]),
    transition(':leave', [animate('0.5s', style({ opacity: 0 }))]),
  ]);

  public static getCopy(object: any): any {
    if (object) {
      return JSON.parse(JSON.stringify(object));
    }
    return undefined;
  }

  public static async wait(ms: number = 3000) {
    await new Promise((resolve) => setTimeout(resolve, ms));
  }

  public static loadScript(id: string, src: string, tag: string = 'head') {
    const script = document.createElement('script');
    script.src = src;
    script.id = id;
    script.type = 'text/javascript';
    script.async = true;
    script.charset = 'utf-8';
    document.getElementsByTagName(tag)[0].appendChild(script);
  }

  public static unloadScript(id: string) {
    const script = document.getElementById(id);
    if (script) {
      script.remove();
    }
  }

  public static getMonths(): Array<any> {
    let months = [
      { field: 'January', header: 'Jan' },
      { field: 'February', header: 'Feb' },
      { field: 'March', header: 'Mar' },
      { field: 'April', header: 'Apr' },
      { field: 'May', header: 'May' },
      { field: 'June', header: 'Jun' },
      { field: 'July', header: 'Jul' },
      { field: 'August', header: 'Aug' },
      { field: 'September', header: 'Sep' },
      { field: 'October', header: 'Oct' },
      { field: 'November', header: 'Nov' },
      { field: 'December', header: 'Dec' },
    ];
    return months;
  }

  public static getCycleEndingSalesMonth() {
    const months = {
      Jan: 12,
      Feb: 1,
      Mar: 2,
      Apr: 3,
      May: 4,
      Jun: 5,
      Jul: 6,
      Aug: 7,
      Sep: 8,
      Oct: 9,
      Nov: 10,
      Dec: 11,
    };
    return months;
  }

  public static isEmpty(obj: any) {
    for (let key in obj) {
      if (obj.hasOwnProperty(key)) return false;
    }
    return true;
  }

  public static getMonthYearObject(monthArr: string[], currMonth: string, year: number) {
    // To avoid reference issues
    monthArr = JSON.parse(JSON.stringify(monthArr));
    const index = monthArr.indexOf('December');
    let i = 0;
    const returnArray: any[] = [];

    if (index !== -1) {
      const currMonthIndex = monthArr.indexOf(currMonth);
      if (currMonthIndex > index || currMonthIndex === -1) {
        for (i = 0; i <= index; i++) {
          let y = year - 1;
          returnArray.push({ month: monthArr[i], currYear: y, prevYear: y - 1 });
        }
        if (index < monthArr.length - 1) {
          for (i = index + 1; i < monthArr.length; i++) {
            returnArray.push({ month: monthArr[i], currYear: year, prevYear: year - 1 });
          }
        }
      } else {
        for (i = 0; i <= index; i++) {
          returnArray.push({ month: monthArr[i], currYear: year, prevYear: year - 1 });
        }
        if (index < monthArr.length - 1) {
          for (i = index + 1; i < monthArr.length; i++) {
            returnArray.push({ month: monthArr[i], currYear: year + 1, prevYear: year });
          }
        }
      }
    } else {
      for (i = 0; i < monthArr.length; i++) {
        returnArray.push({ month: monthArr[i], currYear: year, prevYear: year - 1 });
      }
    }
    return returnArray;
  }

  public static getCurrentYear(): number {
    return new Date().getFullYear();
  }

  public static getCurrentMonthNumber(): number {
    return new Date().getMonth() + 1;
  }

  public static getCurrentMonthName(): string {
    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    return monthNames[new Date().getMonth()];
  }

  public static getCurrentMonthShortName(): string {
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sepr', 'Oct', 'Nov', 'Dec'];
    return monthNames[new Date().getMonth()];
  }

  public static getMonthNumber(m: string): number {
    const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    return months.findIndex((month) => month == m) + 1;
  }

  public static getMonthNumberForShortName(m: string): number {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return months.findIndex((month) => month == m) + 1;
  }

  public static getMonthName(monthNumber: number): string {
    const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    return months[monthNumber - 1];
  }

  public static readUploadedFileAsText = (inputFile: any) => {
    const temporaryFileReader = new FileReader();

    return new Promise((resolve, reject) => {
      temporaryFileReader.onerror = () => {
        temporaryFileReader.abort();
        reject(new DOMException('Problem parsing input file.'));
      };

      temporaryFileReader.onload = () => {
        resolve(temporaryFileReader.result);
      };
      temporaryFileReader.readAsText(inputFile);
    });
  };

  public static getDaysOfMonth() {
    const days = [
      { field: 'January', header: '31' },
      { field: 'February', header: '28' },
      { field: 'March', header: '31' },
      { field: 'April', header: '30' },
      { field: 'May', header: '31' },
      { field: 'June', header: '30' },
      { field: 'July', header: '31' },
      { field: 'August', header: '31' },
      { field: 'September', header: '30' },
      { field: 'October', header: '31' },
      { field: 'November', header: '30' },
      { field: 'December', header: '31' },
    ];
    return days;
  }

  public static getYYYYMMDD(d: any) {
    const yyyy = d.getFullYear().toString();
    const mm = (d.getMonth() + 101).toString().slice(-2);
    const dd = (d.getDate() + 100).toString().slice(-2);
    return `${yyyy}-${mm}-${dd}`;
  }

  public static downloadBlobFile(res, inputDocType, outputDocType, filename) {
    let blob = new Blob([res], { type: this.createFileType(inputDocType) });
    const file = new File([blob], filename, { type: this.createFileType(outputDocType) });
    saveAs(file);
  }

  public static createFileType(e): string {
    let fileType: string = '';
    if (e == 'pdf' || e == 'csv') {
      fileType = `application/${e}`;
    } else if (e == 'jpeg' || e == 'jpg' || e == 'png') {
      fileType = `image/${e}`;
    } else if (e == 'txt') {
      fileType = 'text/plain';
    } else if (e == 'ppt' || e == 'pot' || e == 'pps' || e == 'ppa') {
      fileType = 'application/vnd.ms-powerpoint';
    } else if (e == 'pptx') {
      fileType = 'application/vnd.openxmlformats-officedocument.presentationml.presentation';
    } else if (e == 'doc' || e == 'dot') {
      fileType = 'application/msword';
    } else if (e == 'docx') {
      fileType = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
    } else if (e == 'xls' || e == 'xlt' || e == 'xla') {
      fileType = 'application/vnd.ms-excel';
    } else if (e == 'xlsx') {
      fileType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
    }

    return fileType;
  }

  public static numberCommaFormatter(value) {
    if (value) {
      const valueParts = value.toString().split('.');
      const wholePart = valueParts[0];
      const val = wholePart.toString();
      let lastThree = val.substring(val.length - 3);
      const otherNumbers = val.substring(0, val.length - 3);

      if (otherNumbers !== '') {
        lastThree = ',' + lastThree;
      }
      const res = otherNumbers.replace(/\B(?=(\d{3})+(?!\d))/g, ',') + lastThree;
      if (valueParts[1] !== undefined) {
        return `${res}.${valueParts[1].substring(0, 1)}`;
      }
      return `${res}`;
    }
    return value;
  }

  public static sortMonthKeys(monthKeys: string[]): string[] {
    const MONTH_MAP: { [key: string]: number } = {
      Jan: 0,
      Feb: 1,
      Mar: 2,
      Apr: 3,
      May: 4,
      Jun: 5,
      Jul: 6,
      Aug: 7,
      Sep: 8,
      Oct: 9,
      Nov: 10,
      Dec: 11,
    };
    return monthKeys.sort((a, b) => {
      const [monthA, yearA] = [a.slice(0, 3), parseInt(a.slice(4))];
      const [monthB, yearB] = [b.slice(0, 3), parseInt(b.slice(4))];

      const dateA = new Date(2000 + yearA, MONTH_MAP[monthA]);
      const dateB = new Date(2000 + yearB, MONTH_MAP[monthB]);

      return dateA.getTime() - dateB.getTime();
    });
  }

  public static formatMonthYear(input: string): string {
    const match = input.match(/^([A-Za-z]{3})'(\d{2})$/);
    if (!match) return '';
    return `${match[1].toLowerCase()}${match[2]}`;
  }

  public static groupHeadersByMonth(headers: string[]): { [month: string]: string[] } {
    const grouped: { [month: string]: string[] } = {};

    headers.forEach((header) => {
      const parts = header.split('_');
      let monthKey: string;
      let weekKey: string | null = null;

      if (parts.length < 3) {
        const [month, year] = parts;
        monthKey = `${month}'${year}`;
      } else {
        const [month, year, week, dateRange] = parts;
        monthKey = `${month}'${year}`;
        weekKey = dateRange ? `${week}_${dateRange}` : week;
      }

      if (!grouped[monthKey]) grouped[monthKey] = [];
      if (weekKey) grouped[monthKey].push(weekKey);
    });

    return grouped;
  }

  public static updateFrozenColumns(additionalColumn, baseFrozenCols): {
    frozenCols: ColumnDefinition[],
    frozenWidth: string
  } {
    const selectedFields = new Set(additionalColumn.map((col) => col.value));
    const baseColsWithoutNormType = baseFrozenCols.filter((col) => col.field !== 'norm_type');

    const additionalSelectedCols = additionalColumn
      .filter((col) => selectedFields.has(col.value))
      .map((col) => ({
        field: col.value,
        header: col.label,
        width: col.width,
        filterable: col.filterable,
        order: col.order,
      }));

    const normTypeCol: ColumnDefinition = {
      field: 'norm_type',
      header: 'Requirement Signal',
      width: 200,
      filterable: true,
      order: 99,
    };

    const mergedCols: ColumnDefinition[] = [
      ...baseColsWithoutNormType,
      ...additionalSelectedCols,
      normTypeCol
    ];

    const sortedCols = mergedCols.sort((a, b) => (a.order ?? 99) - (b.order ?? 99));
    const totalWidth = sortedCols.reduce((sum, col) => sum + (col.width || 100), 0);

    return {
      frozenCols: sortedCols,
      frozenWidth: `${totalWidth}px`
    };
  }
}
